import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { attractionsAPI } from '../services/api';
import api from '../services/api';
import EnhancedAttractionForm from '../components/EnhancedAttractionForm';
import './Attractions.css';

const Attractions = () => {
  const [attractions, setAttractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAttraction, setEditingAttraction] = useState(null);
  
  // New state for pagination, filtering, and search
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedHalalStatus, setSelectedHalalStatus] = useState('');
  const [filterOptions, setFilterOptions] = useState({
    categories: [],
    districts: [],
    subcategories: []
  });

  // Bulk selection state
  const [selectedAttractions, setSelectedAttractions] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');

  // Tenant config for config-driven language badges and feature flags
  const [tenantLanguages, setTenantLanguages] = useState(['en']);
  const [halalTracking, setHalalTracking] = useState(false);
  useEffect(() => {
    const fetchTenantConfig = async () => {
      try {
        const response = await api.get('/api/v1/config');
        if (response.data?.success) {
          setTenantLanguages(response.data.data.supportedLanguages || ['en']);
          setHalalTracking(response.data.data.features?.halal_tracking === true);
        }
      } catch (e) {
        // fallback defaults
      }
    };
    fetchTenantConfig();
  }, []);

  // Export to CSV handler
  const handleExportCSV = async () => {
    try {
      // Check both 'token' and 'travel-futura-token' for backward compatibility
      const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
      const response = await fetch('/api/attractions/export/csv', {
        method: 'GET',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Export failed' }));
        throw new Error(errorData.message || 'Export failed');
      }

      // Get the blob from response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attractions_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      alert('✅ Attractions exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert(`❌ Failed to export attractions: ${error.message}`);
    }
  };

  // Import CSV: one flow = upload file -> auto preview -> edit -> confirm and import
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [importFileName, setImportFileName] = useState('');

  const triggerImportFileInput = () => {
    const el = document.getElementById('csv-import-input');
    if (el) {
      el.value = '';
      el.click();
    }
  };

  const handleImportFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
      alert('❌ Please select a CSV file');
      return;
    }
    setImportFileName(file.name);
    setPreviewLoading(true);
    try {
      const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
      const formData = new FormData();
      formData.append('csv', file);
      const response = await fetch('/api/attractions/import/csv/preview', {
        method: 'POST',
        headers: { 'Authorization': token ? `Bearer ${token}` : '' },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Preview failed');
      setPreviewData(data.data);
      setShowPreviewModal(true);
    } catch (error) {
      console.error('Preview error:', error);
      alert(`❌ Failed to load preview: ${error.message}`);
    } finally {
      setPreviewLoading(false);
    }
  };

  const updatePreviewUpdate = (updateIndex, field, value) => {
    setPreviewData(prev => {
      if (!prev?.updates?.[updateIndex]) return prev;
      const next = { ...prev, updates: [...prev.updates] };
      next.updates[updateIndex] = {
        ...next.updates[updateIndex],
        proposed: { ...next.updates[updateIndex].proposed, [field]: value }
      };
      return next;
    });
  };

  const updatePreviewCreate = (createIndex, field, value) => {
    setPreviewData(prev => {
      if (!prev?.creates?.[createIndex]) return prev;
      const next = { ...prev, creates: [...prev.creates] };
      next.creates[createIndex] = {
        ...next.creates[createIndex],
        data: { ...next.creates[createIndex].data, [field]: value }
      };
      return next;
    });
  };

  const handleConfirmAndImport = async () => {
    if (!previewData) return;
    const payload = {
      updates: (previewData.updates || []).map(u => ({
        id: u.id,
        name: u.name,
        proposed: u.proposed
      })),
      creates: (previewData.creates || []).map(c => ({
        name: c.name,
        data: c.data
      }))
    };
    if (payload.updates.length === 0 && payload.creates.length === 0) {
      alert('Nothing to import.');
      return;
    }
    setImporting(true);
    try {
      const token = localStorage.getItem('travel-futura-token') || localStorage.getItem('token');
      const response = await fetch('/api/attractions/import/apply', {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Import failed');
      setImportResults(data.data);
      setShowPreviewModal(false);
      setPreviewData(null);
      setImportFileName('');
      setShowImportModal(true);
      const fileInput = document.getElementById('csv-import-input');
      if (fileInput) fileInput.value = '';
      fetchAttractions();
    } catch (error) {
      console.error('Import error:', error);
      alert(`❌ Import failed: ${error.message}`);
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    fetchAttractions();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchAttractions();
    }, searchTerm ? 300 : 0); // 300ms delay for search, immediate for other filters

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, itemsPerPage, searchTerm, selectedCategory, selectedDistrict, selectedHalalStatus, sortField, sortDirection]);

  // Effect to handle page boundary adjustments after deletion
  useEffect(() => {
    // Only refetch if we're not in the middle of a search/filter operation
    if (!searchTerm && !selectedCategory && !selectedDistrict && !selectedHalalStatus) {
      fetchAttractions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchAttractions = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * itemsPerPage;
      const params = {
        limit: itemsPerPage,
        offset: offset,
        sort: sortField,
        sortDirection: sortDirection,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory && { category: selectedCategory }),
        ...(selectedDistrict && { district: selectedDistrict }),
        ...(selectedHalalStatus && { halal_status: selectedHalalStatus })
      };
      
      const response = await attractionsAPI.getAll(params);
      setAttractions(response.data || []);
      setTotalItems(response.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch attractions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await attractionsAPI.getFilterOptions();
      setFilterOptions(response.data || { categories: [], districts: [], subcategories: [] });
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    }
  };

  // Handler functions for search, filtering, and pagination
  const handleSearchChange = (e) => {
    const value = e.target.value;
    console.log('🔍 Search input changed:', value);
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleDistrictChange = (e) => {
    setSelectedDistrict(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleHalalStatusChange = (e) => {
    setSelectedHalalStatus(e.target.value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedDistrict('');
    setSelectedHalalStatus('');
    setCurrentPage(1);
  };

  // Calculate pagination info
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Handle save from EnhancedAttractionForm (after translation workflow)
  const handleSaveAttraction = async (formData) => {
    try {
      console.log('🔍 handleSaveAttraction received data:', formData);
      
      // Check if this is already processed data from translation API
      // (has attractionId field) or raw form data
      if (formData.attractionId) {
        // This is already processed and saved data from translation API
        // Just close the modal and refresh
        console.log('✅ Data already saved via translation API, just refreshing...');
        setShowModal(false);
        setEditingAttraction(null);
        fetchAttractions();
        alert('Attraction saved successfully with translations!');
        return;
      }

      // This is raw form data - process it for direct API call
      console.log('📝 Processing raw form data...');
      
      // Process operating hours - convert to API format
      const processedOperatingHours = {};
      if (formData.operating_hours) {
      Object.keys(formData.operating_hours).forEach(day => {
        const dayData = formData.operating_hours[day];
        if (dayData.is_closed) {
          processedOperatingHours[day] = 'Closed';
        } else if (dayData.hours) {
          let hours = dayData.hours;
          if (dayData.break_period) {
            hours += ` (Break: ${dayData.break_period})`;
          }
          processedOperatingHours[day] = hours;
        }
      });
      }

      // Convert string coordinates to numbers
      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        price_adult: formData.price_adult ? parseFloat(formData.price_adult) : undefined,
        price_child: formData.price_child ? parseFloat(formData.price_child) : undefined,
        operating_hours: processedOperatingHours,
        // Filter out empty media links
        media_links: formData.media_links ? formData.media_links.filter(media => media.url && media.url.trim() !== '') : []
      };

      // Remove empty fields
      Object.keys(submitData).forEach(key => {
        if (submitData[key] === '' || submitData[key] === undefined) {
          delete submitData[key];
        }
      });

      if (editingAttraction) {
        await attractionsAPI.update(editingAttraction.id, submitData);
        alert('Attraction updated successfully with translations!');
      } else {
        await attractionsAPI.create(submitData);
        alert('Attraction created successfully with translations!');
      }
      
      setShowModal(false);
      setEditingAttraction(null);
      fetchAttractions();
    } catch (error) {
      console.error('Failed to save attraction:', error);
      alert('Failed to save attraction. Please check the console for details.');
    }
  };

  const editAttraction = (attraction) => {
    setEditingAttraction(attraction);
    setShowModal(true);
  };

  const deleteAttraction = async (id) => {
    if (window.confirm('Are you sure you want to delete this attraction?')) {
      try {
        await attractionsAPI.delete(id);
        
        // Check if we need to adjust the current page after deletion
        const newTotalItems = totalItems - 1;
        const maxPage = Math.ceil(newTotalItems / itemsPerPage);
        
        // If current page is now beyond the max page, go to the last valid page
        if (currentPage > maxPage && maxPage > 0) {
          setCurrentPage(maxPage);
        } else {
          // Otherwise, just refetch the current page
          fetchAttractions();
        }
        
        alert('Attraction deleted successfully!');
      } catch (error) {
        console.error('Failed to delete attraction:', error);
        alert('Failed to delete attraction. Please try again.');
      }
    }
  };

  const handleTopAttractionToggle = async (id, isTop) => {
    try {
      await attractionsAPI.update(id, { is_top_attraction: isTop });
      // Update the local state immediately for better UX
      setAttractions(prev => prev.map(attraction => 
        attraction.id === id 
          ? { ...attraction, is_top_attraction: isTop }
          : attraction
      ));
    } catch (error) {
      console.error('Failed to update top attraction status:', error);
      alert('Failed to update top attraction status. Please try again.');
    }
  };

  // Bulk selection functions
  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedAttractions(new Set());
      setIsAllSelected(false);
    } else {
      const allIds = new Set(attractions.map(attraction => attraction.id));
      setSelectedAttractions(allIds);
      setIsAllSelected(true);
    }
  };

  const handleSelectAttraction = (id) => {
    const newSelected = new Set(selectedAttractions);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedAttractions(newSelected);
    setIsAllSelected(newSelected.size === attractions.length && attractions.length > 0);
  };

  const handleBulkDelete = async () => {
    if (selectedAttractions.size === 0) {
      alert('Please select attractions to delete.');
      return;
    }

    const confirmMessage = `Are you sure you want to delete ${selectedAttractions.size} attraction(s)? This action cannot be undone.`;
    if (window.confirm(confirmMessage)) {
      try {
        const selectedIds = Array.from(selectedAttractions);
        const results = await Promise.allSettled(
          selectedIds.map(id => attractionsAPI.delete(id))
        );
        
        // Count successful and failed deletions
        const successful = results.filter(result => result.status === 'fulfilled').length;
        const failed = results.filter(result => result.status === 'rejected').length;
        
        // Log failed deletions for debugging
        const failedResults = results.filter(result => result.status === 'rejected');
        if (failedResults.length > 0) {
          console.error('Failed deletions:', failedResults.map(r => r.reason));
        }
        
        // Check if we need to adjust the current page after bulk deletion
        const newTotalItems = totalItems - successful;
        const maxPage = Math.ceil(newTotalItems / itemsPerPage);
        
        // If current page is now beyond the max page, go to the last valid page
        if (currentPage > maxPage && maxPage > 0) {
          setCurrentPage(maxPage);
        } else {
          // Otherwise, just refetch the current page
          fetchAttractions();
        }
        
        setSelectedAttractions(new Set());
        setIsAllSelected(false);
        
        // Show appropriate success/error message
        if (successful === selectedIds.length) {
          alert(`Successfully deleted ${successful} attraction(s).`);
        } else if (successful > 0) {
          alert(`Successfully deleted ${successful} attraction(s). ${failed} deletion(s) failed.`);
        } else {
          alert('Failed to delete attractions. Please try again.');
        }
      } catch (error) {
        console.error('Failed to delete attractions:', error);
        alert('Failed to delete attractions. Please try again.');
      }
    }
  };

  // Sorting functions
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting changes
  };


  const getSortIcon = (field) => {
    if (sortField !== field) {
      return '↕️';
    }
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getTranslationStatusBadge = (attraction) => {
    // Use the backend's calculated translation status directly
    const actualStatus = attraction.translation_status || 'pending';

    // Calculate completion percentage from translations JSON column + base fields
    const nonBaseLangs = tenantLanguages.filter(c => c !== 'en');
    const coreFields = ['name', 'description'];
    const translations = attraction.translations || {};

    let totalCoreFields = 0;
    let completedCoreFields = 0;

    // English (base) — check base columns
    for (const field of coreFields) {
      totalCoreFields++;
      const val = attraction[field];
      if (val && typeof val === 'string' && val.trim() !== '') {
        completedCoreFields++;
      }
    }

    // Non-English — check translations JSON
    for (const lang of nonBaseLangs) {
      for (const field of coreFields) {
        totalCoreFields++;
        const val = translations[lang]?.[field];
        if (val && typeof val === 'string' && val.trim() !== '') {
          completedCoreFields++;
        }
      }
    }

    const completionPercentage = totalCoreFields > 0 ? Math.round((completedCoreFields / totalCoreFields) * 100) : 100;
    
    const statusConfig = {
      'completed': { text: 'Completed', className: 'status-completed', icon: '✅' },
      'translating': { text: 'Translating', className: 'status-translating', icon: '🔄' },
      'pending': { text: 'Pending', className: 'status-pending', icon: '⏳' },
      'failed': { text: 'Failed', className: 'status-failed', icon: '❌' }
    };

    const config = statusConfig[actualStatus] || { text: 'Unknown', className: 'status-unknown', icon: '❓' };
    
    return (
      <span className={`translation-badge ${config.className}`} title={`Translation Status: ${config.text} (${completionPercentage}% core fields completed)`}>
        <span className="status-icon">{config.icon}</span>
        <span className="status-text">{config.text}</span>
      </span>
    );
  };

  return (
    <Layout title="Attractions">
      <div className="attractions">
        <div className="attractions-header">
          <h2>Manage Attractions</h2>
          <div className="header-actions">
            <div className="import-section" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
              <input
                type="file"
                id="csv-import-input"
                accept=".csv"
                onChange={handleImportFileSelect}
                style={{ display: 'none' }}
              />
              <button
                className="import-btn"
                onClick={triggerImportFileInput}
                disabled={previewLoading}
                title="Upload CSV → preview → edit → confirm and import"
              >
                {previewLoading ? '⏳ Loading preview...' : '📥 Import CSV'}
              </button>
            </div>
            <button 
              className="export-btn"
              onClick={handleExportCSV}
              title="Export all attractions to CSV"
            >
              📥 Export to CSV
            </button>
            <button 
              className="add-btn"
              onClick={() => {
                setEditingAttraction(null);
                setShowModal(true);
              }}
            >
              Add New Attraction
            </button>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="attractions-controls">
          <div className="search-section">
            <input
              type="text"
              placeholder="Search attractions by name, description, address, or category..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>
          
          <div className="filter-section">
            <select
              value={selectedCategory}
              onChange={handleCategoryChange}
              className="filter-select"
            >
              <option value="">All Categories</option>
              {filterOptions.categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            
            <select
              value={selectedDistrict}
              onChange={handleDistrictChange}
              className="filter-select"
            >
              <option value="">All Districts</option>
              {filterOptions.districts.map(district => (
                <option key={district} value={district}>{district}</option>
              ))}
            </select>
            
            {halalTracking && (
              <select
                value={selectedHalalStatus}
                onChange={handleHalalStatusChange}
                className="filter-select"
              >
                <option value="">All Halal Status</option>
                <option value="halal">Halal</option>
                <option value="non-halal">Non-Halal</option>
                <option value="unknown">Unknown</option>
                <option value="muslim-friendly">Muslim-Friendly</option>
                <option value="vegetarian">Vegetarian</option>
                <option value="vegan">Vegan</option>
              </select>
            )}
            
            <select
              value={itemsPerPage}
              onChange={handleItemsPerPageChange}
              className="filter-select"
            >
              <option value={20}>20 per page</option>
              <option value={50}>50 per page</option>
              <option value={100}>100 per page</option>
            </select>
            
            <button onClick={clearFilters} className="clear-filters-btn">
              Clear Filters
            </button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          {totalItems > 0 ? (
            <p>Showing {startItem}-{endItem} of {totalItems} attractions</p>
          ) : (
            <p>No attractions found</p>
          )}
        </div>

        {/* Bulk Actions */}
        {selectedAttractions.size > 0 && (
          <div className="bulk-actions">
            <div className="bulk-actions-info">
              <span>{selectedAttractions.size} attraction(s) selected</span>
            </div>
            <div className="bulk-actions-buttons">
              <button 
                className="bulk-delete-btn"
                onClick={handleBulkDelete}
              >
                🗑️ Delete Selected
              </button>
              <button 
                className="bulk-clear-btn"
                onClick={() => {
                  setSelectedAttractions(new Set());
                  setIsAllSelected(false);
                }}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading">Loading attractions...</div>
        ) : (
          <>
            <div className="attractions-table">
            <table>
              <thead>
                <tr>
                  <th className="checkbox-column">
                    <label className="select-all-checkbox">
                      <input
                        type="checkbox"
                        checked={isAllSelected}
                        onChange={handleSelectAll}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </th>
                  <th 
                    className="sortable-column" 
                    onClick={() => handleSort('name')}
                    title="Click to sort by name"
                  >
                    Name {getSortIcon('name')}
                  </th>
                  <th 
                    className="sortable-column" 
                    onClick={() => handleSort('category')}
                    title="Click to sort by category"
                  >
                    Category {getSortIcon('category')}
                  </th>
                  <th 
                    className="sortable-column" 
                    onClick={() => handleSort('district')}
                    title="Click to sort by district"
                  >
                    District {getSortIcon('district')}
                  </th>
                  <th 
                    className="sortable-column" 
                    onClick={() => handleSort('created_at')}
                    title="Click to sort by date and time of entry"
                  >
                    Date and Time of Entry {getSortIcon('created_at')}
                  </th>
                  <th 
                    className="sortable-column" 
                    onClick={() => handleSort('is_top_attraction')}
                    title="Click to sort by top attraction status"
                  >
                    Top Attraction {getSortIcon('is_top_attraction')}
                  </th>
                  <th 
                    className="sortable-column" 
                    onClick={() => handleSort('translation_status')}
                    title="Click to sort by translation status"
                  >
                    Translation Status {getSortIcon('translation_status')}
                  </th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {attractions.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '2rem' }}>
                      {searchTerm || selectedCategory || selectedDistrict || selectedHalalStatus
                        ? 'No attractions found matching your criteria. Try adjusting your filters.'
                        : 'No attractions found. Click "Add New Attraction" to create one.'
                      }
                    </td>
                  </tr>
                ) : (
                  attractions.map((attraction) => (
                    <tr key={attraction.id} className={selectedAttractions.has(attraction.id) ? 'selected-row' : ''}>
                      <td className="checkbox-column">
                        <label className="row-checkbox">
                          <input
                            type="checkbox"
                            checked={selectedAttractions.has(attraction.id)}
                            onChange={() => handleSelectAttraction(attraction.id)}
                          />
                          <span className="checkmark"></span>
                        </label>
                      </td>
                      <td>{attraction.name}</td>
                      <td>{attraction.category}</td>
                      <td>{attraction.district}</td>
                      <td>{attraction.created_at ? new Date(attraction.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'N/A'}</td>
                      <td>
                        <label className="top-attraction-checkbox">
                          <input
                            type="checkbox"
                            checked={attraction.is_top_attraction || false}
                            onChange={(e) => handleTopAttractionToggle(attraction.id, e.target.checked)}
                          />
                          <span className="checkmark"></span>
                        </label>
                      </td>
                      <td>
                        <div className="translation-status">
                          {getTranslationStatusBadge(attraction)}
                        </div>
                      </td>
                      <td>
                          <button 
                            className="edit-btn"
                          onClick={() => editAttraction(attraction)}
                          >
                            Edit
                          </button>
                          <button 
                            className="delete-btn"
                          onClick={() => deleteAttraction(attraction.id)}
                          >
                            Delete
                          </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="pagination-btn"
                >
                  Previous
                </button>
                
                <div className="pagination-info">
                  Page {currentPage} of {totalPages}
                </div>
                
                {/* Page numbers */}
                <div className="page-numbers">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="pagination-btn"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Enhanced Multi-Language Attraction Form */}
        <EnhancedAttractionForm
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingAttraction(null);
          }}
          onSave={handleSaveAttraction}
          editingAttraction={editingAttraction}
          halalTracking={halalTracking}
        />

        {/* Import Preview Modal – upload → preview → edit → confirm */}
        {showPreviewModal && previewData && (
          <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div className="modal-header">
                <h3>Import preview{importFileName ? ` — ${importFileName}` : ''}</h3>
                <button className="close-btn" onClick={() => setShowPreviewModal(false)}>×</button>
              </div>
              <div className="modal-body" style={{ overflow: 'auto', flex: 1 }}>
                <p style={{ marginBottom: '12px', fontSize: '13px', color: '#555' }}>
                  No data has been saved yet. Edit below if needed, then confirm. Images are never modified by import — add or edit images in the CMS.
                </p>
                <div style={{ marginBottom: '16px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <span><strong>Total rows:</strong> {previewData.summary?.totalRows ?? 0}</span>
                  <span style={{ color: 'blue' }}><strong>To update:</strong> {previewData.summary?.toUpdate ?? 0}</span>
                  <span style={{ color: 'green' }}><strong>To create:</strong> {previewData.summary?.toCreate ?? 0}</span>
                  {previewData.summary?.errorCount > 0 && (
                    <span style={{ color: 'red' }}><strong>Errors:</strong> {previewData.summary.errorCount}</span>
                  )}
                </div>

                {previewData.updates && previewData.updates.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '8px' }}>Existing attractions to be updated ({previewData.updates.length}) — editable</h4>
                    <div style={{ maxHeight: '280px', overflowY: 'auto', fontSize: '12px', border: '1px solid #eee', borderRadius: '6px', padding: '10px' }}>
                      {previewData.updates.map((u, idx) => (
                        <div key={idx} style={{ padding: '10px', borderBottom: idx < previewData.updates.length - 1 ? '1px solid #eee' : 'none' }}>
                          <div style={{ fontWeight: 600, marginBottom: '8px' }}>Row {u.rowNumber}: {u.name} <span style={{ color: '#666', fontWeight: 400 }}>(ID: {u.id})</span></div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                            {['name', 'category', 'address', 'address_1', 'address_2', 'city', 'postal_code', 'district', 'latitude', 'longitude', 'contact_number'].map(field => (
                              <label key={field} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontSize: '11px', color: '#666' }}>{field}</span>
                                <input
                                  type="text"
                                  value={u.proposed?.[field] ?? ''}
                                  onChange={e => updatePreviewUpdate(idx, field, e.target.value)}
                                  style={{ padding: '4px 6px', fontSize: '12px' }}
                                />
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {previewData.creates && previewData.creates.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4 style={{ marginBottom: '8px' }}>New attractions to be created ({previewData.creates.length}) — editable</h4>
                    <div style={{ maxHeight: '280px', overflowY: 'auto', fontSize: '12px', border: '1px solid #eee', borderRadius: '6px', padding: '10px' }}>
                      {previewData.creates.map((c, cIdx) => (
                        <div key={cIdx} style={{ padding: '10px', borderBottom: cIdx < previewData.creates.length - 1 ? '1px solid #eee' : 'none' }}>
                          <div style={{ fontWeight: 600, marginBottom: '4px' }}>Row {c.rowNumber}: {c.name}</div>
                          {c.data?.id && (
                            <div style={{ fontSize: '11px', color: '#666', marginBottom: '6px' }}>ID (assigned by backend): <code>{c.data.id}</code></div>
                          )}
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' }}>
                            {['name', 'category', 'subcategory', 'address', 'address_1', 'address_2', 'city', 'postal_code', 'district', 'latitude', 'longitude', 'contact_number'].map(field => (
                              <label key={field} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{ fontSize: '11px', color: '#666' }}>{field}</span>
                                <input
                                  type="text"
                                  value={c.data?.[field] ?? ''}
                                  onChange={e => updatePreviewCreate(cIdx, field, e.target.value)}
                                  style={{ padding: '4px 6px', fontSize: '12px' }}
                                />
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {previewData.errors && previewData.errors.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ color: 'red', marginBottom: '6px' }}>Errors ({previewData.errors.length})</h4>
                    <div style={{ maxHeight: '120px', overflowY: 'auto', fontSize: '12px', color: 'red' }}>
                      {previewData.errors.map((err, idx) => (
                        <div key={idx}>Row {err.row}: {err.name} — {err.error}</div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer" style={{ flexShrink: 0 }}>
                <button className="btn" onClick={() => setShowPreviewModal(false)}>Cancel</button>
                <button
                  className="btn btn-primary"
                  onClick={handleConfirmAndImport}
                  disabled={importing || ((previewData.updates?.length || 0) + (previewData.creates?.length || 0)) === 0}
                >
                  {importing ? '⏳ Importing...' : 'Confirm and import'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Results Modal */}
        {showImportModal && importResults && (
          <div className="modal-overlay" onClick={() => setShowImportModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
              <div className="modal-header">
                <h3>Import Results</h3>
                <button className="close-btn" onClick={() => setShowImportModal(false)}>×</button>
              </div>
              <div className="modal-body">
                <div style={{ marginBottom: '20px' }}>
                  <p><strong>Total Processed:</strong> {importResults.total}</p>
                  <p style={{ color: 'green' }}><strong>Created:</strong> {importResults.created}</p>
                  <p style={{ color: 'blue' }}><strong>Updated:</strong> {importResults.updated}</p>
                  <p style={{ color: 'orange' }}><strong>Skipped:</strong> {importResults.skipped}</p>
                </div>

                {importResults.duplicates && importResults.duplicates.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h4>Processed ({importResults.duplicates.length}):</h4>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '12px' }}>
                      {importResults.duplicates.map((dup, idx) => (
                        <div key={idx} style={{ padding: '5px', borderBottom: '1px solid #eee' }}>
                          {dup.row != null ? `Row ${dup.row}: ` : ''}{dup.name} — {dup.action === 'updated' ? 'Updated' : dup.action === 'created' ? 'Created' : 'Skipped'}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {importResults.errors && importResults.errors.length > 0 && (
                  <div>
                    <h4 style={{ color: 'red' }}>Errors ({importResults.errors.length}):</h4>
                    <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '12px' }}>
                      {importResults.errors.map((error, idx) => (
                        <div key={idx} style={{ padding: '5px', borderBottom: '1px solid #eee', color: 'red' }}>
                          {error.row != null ? `Row ${error.row}: ` : ''}{error.name} — {error.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-primary" onClick={() => {
                  setShowImportModal(false);
                  setImportResults(null);
                }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Attractions; 